import {
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiToolTip,
} from "@elastic/eui";
import { OpsanioWaves } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { AnketaResourcesEnum } from "src/auth/types";
import { useOpsanioCan } from "../can";
import {
  WaveStep,
  WaveStepKey,
  WaveSummary,
  buildWaveSteps,
  primaryActionFor,
} from "./model";

export type WaveActionName =
  | "prepare-monitoring"
  | "deploy"
  | "merge"
  | "finalize"
  | "update-merge-state";

/**
 * The root wave (dev) has no merge-request chain to walk, so the workflow
 * sequence does not apply to it - but its pinned version set still has to be
 * editable, which is why it gets its own small action bar instead of nothing.
 */
export const WaveRootActionsPanel = ({
  onEdit,
}: {
  onEdit: () => void;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const can = useOpsanioCan();
  return (
    <EuiPanel hasBorder paddingSize="l" hasShadow={false}>
      <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
        {can(AnketaResourcesEnum.opsanioUpdates, "update") && (
          <EuiFlexItem grow={false}>
            <EuiButton size="s" iconType="pencil" onClick={onEdit}>
              {translate("anketa.opsanio.waves.edit_versions", {
                defaultMessage: "Edit versions",
              })}
            </EuiButton>
          </EuiFlexItem>
        )}
        <EuiFlexItem style={{ minWidth: 160 }}>
          <EuiText size="xs" color="subdued">
            {translate("anketa.opsanio.waves.hint.root", {
              defaultMessage:
                "dev tracks floating tags. Versions are pinned from main onwards.",
            })}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiPanel>
  );
};

/**
 * The deployment workflow as an explicit sequence.
 *
 * The six operations used to sit in a flat row of mixed button weights gated by
 * three different conditions, which gave no sense of where a wave is or why a
 * control is greyed out. Here each step carries its own state, the recommended
 * next action is promoted to a single primary button, and steps GitLab cannot
 * report on are labelled as such instead of being guessed at.
 */
export const WaveWorkflowPanel = ({
  summary,
  actionInFlight,
  onCreateUpdate,
  onEdit,
  onNotify,
  runWaveAction,
}: {
  summary: WaveSummary;
  actionInFlight: string | null;
  onCreateUpdate: () => void;
  onEdit: () => void;
  onNotify: () => void;
  runWaveAction: (
    wave: OpsanioWaves,
    action: WaveActionName,
    label: string,
  ) => Promise<void>;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const steps = buildWaveSteps(summary);
  // Wave operations (steps, merge, finalize, notify, merge request) are
  // `opsanio/updates · create` on the server and act on every appliance of the
  // wave, so they also need `opsanio/fleet · view`; pinning versions is `update`.
  const can = useOpsanioCan();
  const canOperate =
    can(AnketaResourcesEnum.opsanioUpdates, "create") &&
    can(AnketaResourcesEnum.opsanioFleet, "view", { isInternal: true });
  const canEditVersions = can(AnketaResourcesEnum.opsanioUpdates, "update");
  const noPermission = translate("anketa.permission.denied.action", {
    defaultMessage: "You do not have permission for this action",
  });

  const stepLabel = (key: WaveStepKey): string =>
    ({
      create: translate("anketa.opsanio.waves.create_update", {
        defaultMessage: "Create update",
      }),
      "prepare-monitoring": translate(
        "anketa.opsanio.waves.action.prepare_monitoring",
        { defaultMessage: "Prepare monitoring" },
      ),
      deploy: translate("anketa.opsanio.waves.action.deploy", {
        defaultMessage: "Deploy",
      }),
      "update-merge-state": translate(
        "anketa.opsanio.waves.action.update_merge_state",
        { defaultMessage: "Update merge state" },
      ),
      merge: translate("anketa.opsanio.waves.action.merge_update", {
        defaultMessage: "Merge update",
      }),
      finalize: translate("anketa.opsanio.waves.action.finalize", {
        defaultMessage: "Finalize",
      }),
    })[key];

  const stepNote = (step: WaveStep): string => {
    if (step.status === "complete")
      return translate("anketa.opsanio.waves.step.done", {
        defaultMessage: "Done",
      });
    if (step.status === "current")
      return translate("anketa.opsanio.waves.step.current", {
        defaultMessage: "Current step",
      });
    if (step.untracked)
      return translate("anketa.opsanio.waves.step.untracked", {
        defaultMessage: "Manual · not tracked",
      });
    if (step.key === "create")
      return summary.state === "blocked"
        ? translate("anketa.opsanio.waves.state.blocked", {
            defaultMessage: "Blocked",
          })
        : translate("anketa.opsanio.waves.step.waiting", {
            defaultMessage: "Waiting",
          });
    return translate("anketa.opsanio.waves.step.needs_open_update", {
      defaultMessage: "Needs open update",
    });
  };

  const runStep = (key: WaveStepKey): void => {
    if (key === "create") {
      onCreateUpdate();
      return;
    }
    runWaveAction(summary.wave, key as WaveActionName, stepLabel(key));
  };

  const primary = primaryActionFor(summary);
  const primaryBusy =
    actionInFlight === `${summary.wave}:merge` ||
    actionInFlight === `${summary.wave}:finalize`;

  const primaryConfig: {
    label: string;
    help: string;
    color: "primary" | "success";
    icon: string;
    disabled: boolean;
    onClick: () => void;
  } = {
    "create-version": {
      label: translate("anketa.opsanio.create", {
        defaultMessage: "Create version set",
      }),
      help: translate("anketa.opsanio.waves.hint.unconfigured", {
        defaultMessage: "No version set has been pinned for this wave yet.",
      }),
      color: "success" as const,
      icon: "plusInCircle",
      disabled: false,
      onClick: onEdit,
    },
    "create-update": {
      label: translate("anketa.opsanio.waves.create_update", {
        defaultMessage: "Create update",
      }),
      help:
        summary.state === "blocked"
          ? translate("anketa.opsanio.waves.hint.blocked", {
              defaultMessage:
                "The parent wave must be up to date and free of in-flight updates.",
            })
          : translate("anketa.opsanio.waves.hint.updates", {
              defaultMessage:
                "Creating an update opens a merge request on the FluxCD config repo.",
            }),
      color: "primary" as const,
      icon: "merge",
      disabled: !summary.parentReady,
      onClick: onCreateUpdate,
    },
    merge: {
      label: translate("anketa.opsanio.waves.action.merge_update", {
        defaultMessage: "Merge update",
      }),
      help: translate("anketa.opsanio.waves.hint.inflight", {
        defaultMessage:
          "Merging rolls the pinned versions out to every appliance on this wave.",
      }),
      color: "primary" as const,
      icon: "merge",
      disabled: false,
      onClick: () =>
        runWaveAction(
          summary.wave,
          "merge",
          translate("anketa.opsanio.waves.action.merge_update", {
            defaultMessage: "Merge update",
          }),
        ),
    },
    finalize: {
      label: translate("anketa.opsanio.waves.action.finalize", {
        defaultMessage: "Finalize",
      }),
      help: translate("anketa.opsanio.waves.hint.finalize", {
        defaultMessage:
          "Ends maintenance mode and closes the update for this wave.",
      }),
      color: "success" as const,
      icon: "checkInCircleFilled",
      disabled: false,
      onClick: () =>
        runWaveAction(
          summary.wave,
          "finalize",
          translate("anketa.opsanio.waves.action.finalize", {
            defaultMessage: "Finalize",
          }),
        ),
    },
    none: {
      label: translate("anketa.opsanio.waves.nothing_to_promote", {
        defaultMessage: "Nothing to promote",
      }),
      help: translate("anketa.opsanio.waves.hint.current", {
        defaultMessage: "Nothing to promote - this wave matches its parent.",
      }),
      color: "primary" as const,
      icon: "check",
      disabled: true,
      onClick: () => undefined,
    },
  }[primary];
  const primaryAllowed =
    primary === "none" ||
    (primary === "create-version" ? canEditVersions : canOperate);

  const nextStep = steps.find((s) => s.status === "current");
  const workflowHint = nextStep
    ? translate("anketa.opsanio.waves.workflow.next", {
        values: { step: stepLabel(nextStep.key) },
        defaultMessage: "Next: {{step}}",
      })
    : translate("anketa.opsanio.waves.step.done", { defaultMessage: "Done" });

  return (
    <EuiPanel hasBorder paddingSize="l" hasShadow={false}>
      <EuiFlexGroup
        alignItems="baseline"
        justifyContent="spaceBetween"
        gutterSize="s"
        responsive={false}
      >
        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued">
            <strong>
              {translate("anketa.opsanio.waves.workflow_label", {
                defaultMessage: "DEPLOYMENT WORKFLOW",
              })}
            </strong>
          </EuiText>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiText size="xs" color="subdued">
            {workflowHint}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="m" />

      <EuiFlexGroup gutterSize="none" responsive={false} alignItems="flexStart">
        {steps.map((step, index) => {
          const busy = actionInFlight === `${summary.wave}:${step.key}`;
          const clickable = step.status !== "disabled" && canOperate;
          return (
            <EuiFlexItem key={step.key} style={{ minWidth: 0 }}>
              <EuiFlexGroup
                gutterSize="none"
                alignItems="center"
                responsive={false}
              >
                <EuiFlexItem grow={false}>
                  {busy ? (
                    <EuiLoadingSpinner size="m" />
                  ) : (
                    <EuiBadge
                      color={
                        step.status === "complete"
                          ? "success"
                          : step.status === "current"
                            ? "primary"
                            : step.status === "incomplete"
                              ? "hollow"
                              : "default"
                      }
                      iconType={
                        step.status === "complete" ? "check" : undefined
                      }
                    >
                      {index + 1}
                    </EuiBadge>
                  )}
                </EuiFlexItem>
                {index < steps.length - 1 && (
                  <EuiFlexItem>
                    <EuiHorizontalRule margin="none" />
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
              <EuiSpacer size="xs" />
              <div style={{ paddingRight: 12 }}>
                {clickable ? (
                  <EuiButtonEmpty
                    size="xs"
                    flush="left"
                    isLoading={busy}
                    onClick={() => runStep(step.key)}
                  >
                    {stepLabel(step.key)}
                  </EuiButtonEmpty>
                ) : (
                  <EuiText size="xs" color="subdued">
                    <strong>{stepLabel(step.key)}</strong>
                  </EuiText>
                )}
                <EuiText size="xs" color="subdued">
                  {stepNote(step)}
                </EuiText>
              </div>
            </EuiFlexItem>
          );
        })}
      </EuiFlexGroup>

      <EuiHorizontalRule margin="m" />

      <EuiFlexGroup gutterSize="s" alignItems="center" wrap responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiToolTip
            content={primaryAllowed ? primaryConfig.help : noPermission}
          >
            <EuiButton
              fill
              size="s"
              color={primaryConfig.color}
              iconType={primaryConfig.icon}
              isDisabled={primaryConfig.disabled || !primaryAllowed}
              isLoading={primaryBusy}
              onClick={primaryConfig.onClick}
            >
              {primaryConfig.label}
            </EuiButton>
          </EuiToolTip>
        </EuiFlexItem>
        <EuiFlexItem style={{ minWidth: 160 }}>
          <EuiText size="xs" color="subdued">
            {primaryConfig.help}
          </EuiText>
        </EuiFlexItem>
        {summary.merge?.web_url && (
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              size="s"
              iconType="popout"
              href={summary.merge.web_url}
              target="_blank"
            >
              {translate("anketa.opsanio.documentation.open.gitlab", {
                defaultMessage: "Open in GitLab",
              })}
            </EuiButtonEmpty>
          </EuiFlexItem>
        )}
        {canOperate && (
        <EuiFlexItem grow={false}>
          <EuiToolTip
            content={translate("anketa.opsanio.waves.action.notify.tooltip", {
              defaultMessage:
                "Send a free-text information email to each appliance on this wave (one dedicated mail per appliance, to its update-notification address with technical + sales in CC).",
            })}
          >
            <EuiButtonEmpty
              size="s"
              color="text"
              iconType="email"
              isDisabled={summary.appliances.length === 0}
              isLoading={actionInFlight === `${summary.wave}:notify`}
              onClick={onNotify}
            >
              {translate("anketa.opsanio.waves.action.notify", {
                defaultMessage: "Notify customers",
              })}
            </EuiButtonEmpty>
          </EuiToolTip>
        </EuiFlexItem>
        )}
        {canEditVersions && (
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty size="s" color="text" iconType="pencil" onClick={onEdit}>
              {translate("anketa.opsanio.waves.edit_versions", {
                defaultMessage: "Edit versions",
              })}
            </EuiButtonEmpty>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
    </EuiPanel>
  );
};
